/**
 * ACRES Admin — Achievement Tier Manager
 */
import React, { useEffect, useState } from 'react';
import { acresAdmin } from '../../../lib/services/acresService';

interface Tier {
  id?: string; key: string; name: string; description?: string;
  min_points: number; min_tasks: number; bonus_multiplier: number;
  bonus_draw_entries: number; sort_order: number; is_active: boolean;
  icon?: string;
}

const empty: Tier = { key: '', name: '', min_points: 0, min_tasks: 0, bonus_multiplier: 1, bonus_draw_entries: 0, sort_order: 0, is_active: true };

export const AcresAchievementsAdmin: React.FC<{ currentUser?: any }> = () => {
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [editing, setEditing] = useState<Tier | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [awardUserId, setAwardUserId] = useState('');
  const [awardTierId, setAwardTierId] = useState('');
  const [awardReason, setAwardReason] = useState('');

  const load = async () => {
    try { const r = await acresAdmin.listTiers(); setTiers(r.tiers || []); }
    catch (e: any) { setError(e?.message || 'Failed to load tiers'); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing) return;
    if (!editing.key || !editing.name) { alert('Key and name required'); return; }
    try { await acresAdmin.upsertTier(editing); setEditing(null); await load(); }
    catch (e: any) { alert(e?.message || 'Save failed'); }
  };

  const toggle = async (t: Tier) => {
    if (!t.id) return;
    try { await acresAdmin.setTierActive(t.id, !t.is_active); await load(); }
    catch (e: any) { alert(e?.message); }
  };

  const grantManual = async () => {
    const uid = parseInt(awardUserId, 10);
    if (!uid || !awardTierId) { alert('User ID and tier required'); return; }
    if (!confirm(`Manually grant tier to user #${uid}? This is logged in admin_audit_logs.`)) return;
    try { await acresAdmin.manualAward(uid, awardTierId, awardReason || 'Manual grant'); alert('Awarded'); setAwardUserId(''); setAwardReason(''); }
    catch (e: any) { alert(e?.message || 'Award failed'); }
  };

  return (
    <div>
      {error && <div style={errBox}>{error}</div>}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ fontWeight: 600 }}>Achievement Tiers ({tiers.length})</div>
        <button onClick={() => setEditing({ ...empty })} style={btnPrimary}>+ New Tier</button>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={tableStyle}>
          <thead><tr style={trHead}>
            <th style={th}>Key</th><th style={th}>Name</th><th style={th}>Min Pts</th><th style={th}>Min Tasks</th>
            <th style={th}>Multiplier</th><th style={th}>+Draws</th><th style={th}>Active</th><th style={th}></th>
          </tr></thead>
          <tbody>
            {tiers.map(t => (
              <tr key={t.id} style={trBody}>
                <td style={td}>{t.key}</td>
                <td style={td}>{t.icon} {t.name}</td>
                <td style={td}>{t.min_points.toLocaleString()}</td>
                <td style={td}>{t.min_tasks}</td>
                <td style={td}>{Number(t.bonus_multiplier).toFixed(2)}x</td>
                <td style={td}>+{t.bonus_draw_entries}</td>
                <td style={td}>{t.is_active ? '🟢' : '⚪'}</td>
                <td style={td}>
                  <button onClick={() => setEditing(t)} style={btnSmall}>Edit</button>
                  <button onClick={() => toggle(t)} style={btnSmall}>{t.is_active ? 'Deactivate' : 'Activate'}</button>
                </td>
              </tr>
            ))}
            {!tiers.length && <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: '#9CA3AF' }}>No tiers configured</td></tr>}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 24, padding: 14, background: '#0F0F0F', border: '1px solid #1E1E1E', borderRadius: 8 }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Manual Tier Award</div>
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr 120px', gap: 8, alignItems: 'center' }}>
          <input placeholder="User ID" value={awardUserId} onChange={e => setAwardUserId(e.target.value)} style={input} />
          <select value={awardTierId} onChange={e => setAwardTierId(e.target.value)} style={input}>
            <option value="">— Select tier —</option>
            {tiers.filter(t => t.is_active).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <input placeholder="Reason (audit log)" value={awardReason} onChange={e => setAwardReason(e.target.value)} style={input} />
          <button onClick={grantManual} style={btnPrimary}>Grant</button>
        </div>
      </div>

      {editing && (
        <div style={modalOverlay}>
          <div style={modalCard}>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 12 }}>{editing.id ? 'Edit Tier' : 'New Tier'}</div>
            <div style={grid}>
              <Field label="Key (snake_case)*"><input style={input} value={editing.key} onChange={e => setEditing({ ...editing, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })} /></Field>
              <Field label="Name*"><input style={input} value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} /></Field>
              <Field label="Icon"><input style={input} value={editing.icon || ''} placeholder="🥉" onChange={e => setEditing({ ...editing, icon: e.target.value })} /></Field>
              <Field label="Sort order"><input type="number" style={input} value={editing.sort_order} onChange={e => setEditing({ ...editing, sort_order: Number(e.target.value) })} /></Field>
              <Field label="Min points"><input type="number" style={input} value={editing.min_points} onChange={e => setEditing({ ...editing, min_points: Number(e.target.value) })} /></Field>
              <Field label="Min tasks"><input type="number" style={input} value={editing.min_tasks} onChange={e => setEditing({ ...editing, min_tasks: Number(e.target.value) })} /></Field>
              <Field label="Point bonus multiplier"><input type="number" step="0.01" style={input} value={editing.bonus_multiplier} onChange={e => setEditing({ ...editing, bonus_multiplier: Number(e.target.value) })} /></Field>
              <Field label="Bonus draw entries"><input type="number" style={input} value={editing.bonus_draw_entries} onChange={e => setEditing({ ...editing, bonus_draw_entries: Number(e.target.value) })} /></Field>
            </div>
            <Field label="Description"><textarea style={{ ...input, minHeight: 60 }} value={editing.description || ''} onChange={e => setEditing({ ...editing, description: e.target.value })} /></Field>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8 }}>
              <input type="checkbox" checked={editing.is_active} onChange={e => setEditing({ ...editing, is_active: e.target.checked })} /> Active
            </label>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
              <button onClick={() => setEditing(null)} style={btnGhost}>Cancel</button>
              <button onClick={save} style={btnPrimary}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ marginBottom: 8 }}><div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 4 }}>{label}</div>{children}</div>
);
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const trHead: React.CSSProperties = { background: '#1E1E1E' };
const trBody: React.CSSProperties = { borderBottom: '1px solid #1E1E1E' };
const th: React.CSSProperties = { padding: '8px 10px', textAlign: 'left', color: '#F59E0B', fontWeight: 600 };
const td: React.CSSProperties = { padding: '8px 10px' };
const input: React.CSSProperties = { width: '100%', padding: '8px 10px', background: '#0F0F0F', border: '1px solid #2A2A2A', borderRadius: 6, color: '#FFF', fontSize: 13 };
const grid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 };
const modalOverlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
const modalCard: React.CSSProperties = { background: '#121212', border: '1px solid #2A2A2A', borderRadius: 12, padding: 20, width: 'min(640px, 100%)', maxHeight: '90vh', overflowY: 'auto' };
const btnPrimary: React.CSSProperties = { padding: '8px 14px', background: '#F59E0B', color: '#000', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer' };
const btnGhost: React.CSSProperties = { padding: '8px 14px', background: 'transparent', color: '#E5E7EB', border: '1px solid #374151', borderRadius: 6, cursor: 'pointer' };
const btnSmall: React.CSSProperties = { padding: '4px 10px', marginRight: 6, background: 'transparent', color: '#E5E7EB', border: '1px solid #374151', borderRadius: 4, fontSize: 12, cursor: 'pointer' };
const errBox: React.CSSProperties = { padding: 10, background: 'rgba(220,38,38,0.1)', border: '1px solid #DC2626', borderRadius: 6, color: '#FCA5A5', marginBottom: 10 };
